<template>
  <!-- A support conversation, from one side: the messages (older ones load
       as you scroll up), the lines it keeps about itself (resolved,
       reopened), who is typing on the other side, and the composer — or,
       once staff resolve it, what the `resolved` slot says instead. With no
       conversation yet it is a new one: its first message starts it.

       It keeps itself current — at once on a live notice
       (utils/chat_realtime), and by polling under it — and, while it is in
       view, tells the API this side has read what arrived: that clears the
       badges, shows the other side "Seen", and stops the email. A message
       shows the moment it is sent and is confirmed when the API has it; one
       that failed can be sent again. Any message can be replied to — the
       reply quotes it, WhatsApp-style, and its quote leads back to it — and
       reacted to with any emoji. Drop files anywhere on it to attach them.
       The same component lives in the admin and both apps.

       The apps draw it in the popup layout: narrower, with the compact emoji
       grid, and a resolved conversation keeps its composer — the `closed`
       slot stands where it was resolved (the rating card), and writing
       starts a new conversation. -->
  <div
    class="sc-thread"
    :class="{ 'sc-thread--compact': compact, 'sc-thread--popup': popup, 'is-dragging': dragDepth > 0, 'is-resolved': resolved }"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <div ref="viewport" class="sc-thread__viewport">
      <div
        ref="scroller"
        class="sc-thread__scroll"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        :aria-busy="loading ? 'true' : 'false'"
        :aria-label="peerName ? `Conversation with ${peerName}` : 'Conversation'"
        @scroll.passive="onScroll"
        @load.capture="onMediaLoad"
      >
        <div ref="content" class="sc-thread__content">
          <div v-if="loadingOlder" class="sc-thread__older" role="status">
            <span class="sc-spinner" aria-hidden="true"></span>
            <span>Loading earlier messages…</span>
          </div>
          <button v-else-if="hasMore && !loading" type="button" class="sc-thread__older-btn" @click="loadOlder">
            Load earlier messages
          </button>

          <div v-if="loading && !entries.length" class="sc-thread__state" role="status">
            <span class="sc-spinner sc-spinner--lg" aria-hidden="true"></span>
            <span>Loading conversation…</span>
          </div>
          <div v-else-if="loadFailed && !entries.length" class="sc-thread__state">
            <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
            <span>The conversation couldn't be loaded.</span>
            <button type="button" class="sc-thread__retry" @click="load">Try again</button>
          </div>
          <div v-else-if="!entries.length" class="sc-thread__empty">
            <slot name="empty" :insert="insertText"></slot>
          </div>

          <template v-for="entry in entries" :key="entry.key">
            <div v-if="entry.kind === 'day'" class="sc-day" role="separator">
              <span>{{ entry.label }}</span>
            </div>
            <div v-else-if="entry.kind === 'unread'" ref="unread" class="sc-unread" :class="{ 'sc-unread--quiet': entry.quiet }" role="separator">
              <span>New messages</span>
            </div>
            <div v-else-if="entry.kind === 'closed'" class="sc-closed">
              <slot name="closed" :conversation="conversation">
                <div class="sc-event sc-event--resolved" role="note">
                  <span class="sc-event__icon" aria-hidden="true"><i class="pi pi-check"></i></span>
                  <span class="sc-event__text">{{ entry.label }}</span>
                  <span class="sc-event__time">{{ entry.time }}</span>
                </div>
              </slot>
            </div>
            <div v-else-if="entry.kind === 'event'" class="sc-event" :class="`sc-event--${entry.event}`" role="note">
              <span class="sc-event__icon" aria-hidden="true"><i class="pi" :class="entry.icon"></i></span>
              <span class="sc-event__text">{{ entry.label }}</span>
              <span class="sc-event__time">{{ entry.time }}</span>
            </div>
            <chat-message
              v-else
              :message="entry.message"
              :mine="entry.mine"
              :is-me="entry.isMe"
              :first="entry.first"
              :last="entry.last"
              :author-label="entry.authorLabel"
              :avatar="entry.avatar"
              :seen="entry.seen"
              :quote="entry.quote"
              :quote-label="entry.quoteLabel"
              :highlighted="entry.highlighted"
              :closed="resolved"
              @reply="startReply(entry.message)"
              @react="react(entry.message, $event)"
              @more-reactions="openReactionPicker(entry.message, $event)"
              @jump="jumpTo(entry.quote && entry.quote.id)"
              @edit="startEdit(entry.message)"
              @delete="remove(entry.message)"
              @copy="copy(entry.message)"
              @retry="deliver(entry.message)"
              @discard="discard(entry.message)"
              @open-image="lightbox = $event"
            />
          </template>

          <div v-if="typing" class="sc-typing">
            <span class="sc-typing__dots" aria-hidden="true"><i></i><i></i><i></i></span>
            <span>{{ typing.name }} is typing…</span>
          </div>
        </div>
      </div>

      <transition name="sc-fade">
        <button v-if="showJump" type="button" class="sc-jump" @click="scrollToBottom(true)">
          <i class="pi pi-arrow-down" aria-hidden="true"></i>
          <span>{{ jumpCount === 1 ? '1 new message' : jumpCount > 1 ? `${jumpCount} new messages` : 'Latest' }}</span>
        </button>
      </transition>

      <div v-if="dragDepth > 0 && !resolved" class="sc-dropzone" aria-hidden="true">
        <i class="pi pi-cloud-upload"></i>
        <span>Drop files to send</span>
      </div>

      <chat-emoji-picker
        v-if="reactionPicker"
        class="sc-emoji--floating"
        :compact="popup"
        :style="reactionPicker.style"
        @pick="pickReaction"
        @close="reactionPicker = null"
      />
    </div>

    <div v-if="resolved && !popup" class="sc-resolved" role="status">
      <slot name="resolved" :conversation="conversation">
        <span class="sc-resolved__icon" aria-hidden="true"><i class="pi pi-check"></i></span>
        <span class="sc-resolved__text">This conversation has been resolved.</span>
      </slot>
    </div>
    <chat-composer
      v-else
      ref="composer"
      :draft-key="draftKey"
      :placeholder="placeholder"
      :editing="editing"
      :replying="replyBar"
      :show-hint="false"
      :layout="layout"
      @send="send"
      @save-edit="saveEdit"
      @cancel-edit="editing = null"
      @cancel-reply="replyingTo = null"
      @typing="announceTyping"
    />

    <chat-lightbox v-if="lightbox" :images="lightbox.images" :start="lightbox.index" @close="lightbox = null" />
  </div>
</template>
<script>
import { markRaw, reactive } from 'vue';
import chatComposer from '@/components/chat/Chat_Composer.vue';
import chatEmojiPicker from '@/components/chat/Chat_Emoji_Picker.vue';
import chatLightbox from '@/components/chat/Chat_Lightbox.vue';
import chatMessage from '@/components/chat/Chat_Message.vue';
import {
  chatClientId,
  chatClock,
  chatDate,
  chatDayLabel,
  chatFirstName,
  chatPersonName,
  chatQuoteOf,
  chatReactionsAfter,
  CHAT_PAGE_SIZE,
  CHAT_THREAD_LIVE_POLL_MS,
  CHAT_THREAD_POLL_MS,
  deleteChatMessage,
  editChatMessage,
  fetchChatConversation,
  isChatImage,
  isSameDay,
  markChatRead,
  reactToChatMessage,
  sendChatMessage,
} from '@/utils/chat';
import { chatRealtime, onChatEvent, sendChatTyping } from '@/utils/chat_realtime';
import { noteChatRead, refreshChatUnread } from '@/utils/chat_notifications';
import { TOAST_GLOBAL_CONFIG } from '@/utils/const';

// Messages from one person this close together read as one run.
const GROUP_MS = 5 * 60 * 1000;
// How close to the end the conversation must be to follow new messages down.
const STICK_PX = 80;
// How close to the top it must be to load older messages.
const OLDER_PX = 120;
// How long "… is typing" stays without another notice.
const TYPING_SHOWS_MS = 6000;
// A sent message's own copies of its files are kept this long after it is
// confirmed, so its pictures can finish loading from the server first.
const RELEASE_LOCAL_FILES_MS = 60 * 1000;
// The emoji picker a reaction is chosen in (assets/style/chat.css .sc-emoji):
// the full one, the apps' panel's, and the popup layout's compact grid.
const PICKER = { width: 340, height: 360 };
const COMPACT_PICKER = { width: 316, height: 330 };
const POPUP_PICKER = { width: 268, height: 172 };

const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

const byCreatedAt = (a, b) => (chatDate(a.createdAt)?.getTime() || 0) - (chatDate(b.createdAt)?.getTime() || 0);

const hasFiles = (event) => Array.from(event?.dataTransfer?.types || []).includes('Files');

export default {
  name: 'ChatThread',
  components: {
    chatComposer,
    chatEmojiPicker,
    chatLightbox,
    chatMessage,
  },
  props: {
    // The viewer's side: 'staff' (the admin) or 'organization' (the apps).
    side: {
      type: String,
      required: true,
    },
    // The conversation. None: a new one, which its first message starts.
    conversationId: {
      type: String,
      default: null,
    },
    // Staff: the organization the conversation is with — what a new one is
    // started with. The apps leave it out: theirs is always their own.
    organizationId: {
      type: String,
      default: null,
    },
    // In view (the app's chat panel open, the admin's conversation shown):
    // only then is it kept current quickly and marked read.
    active: {
      type: Boolean,
      default: true,
    },
    // The other side, as a whole: an organization's name, or "Screenkode Support".
    peerName: {
      type: String,
      default: '',
    },
    // { name, image, square } beside the other side's messages when they are
    // the team rather than a person (the apps); staff see each person.
    peerAvatar: {
      type: Object,
      default: null,
    },
    // The apps' chat panel: tighter, and no keyboard hint under the box.
    compact: {
      type: Boolean,
      default: false,
    },
    // How it is drawn: 'default' (the admin), or 'popup' (the apps' chat).
    layout: {
      type: String,
      default: 'default',
    },
  },
  // 'restarted': a message written in a resolved conversation (popup layout)
  // started a new one here; 'started' follows once the API has it.
  emits: ['conversation', 'started', 'restarted', 'sent', 'read'],
  data() {
    return {
      // The conversation shown: the one asked for, or the one its first message started.
      threadId: this.conversationId || null,
      messages: [],
      // Messages sent from here that the API has not confirmed yet.
      pending: [],
      conversation: null,
      organization: null,
      loading: true,
      loadFailed: false,
      hasMore: false,
      loadingOlder: false,
      syncedAt: null,
      // Where "new" began when the conversation came into view: the other
      // side's messages after this side's read mark at that moment.
      unreadSince: null,
      showUnread: false,
      stickToBottom: true,
      showJump: false,
      jumpCount: 0,
      typing: null,
      editing: null,
      // The message the next one replies to.
      replyingTo: null,
      // The message just jumped to from a quote, flashing.
      highlightId: null,
      lightbox: null,
      dragDepth: 0,
      // The emoji picker, open for a reaction to one message: { messageId, style }.
      reactionPicker: null,
    };
  },
  computed: {
    account() {
      return this.$store.getters.account?.data || null;
    },
    myId() {
      return this.account?.id || null;
    },
    otherSide() {
      return this.side === 'staff' ? 'organization' : 'staff';
    },
    live() {
      return chatRealtime.live;
    },
    popup() {
      return this.layout === 'popup';
    },
    // Staff closed it: nothing new happens in it until they reopen it.
    resolved() {
      return Boolean(this.conversation && this.conversation.status === 'resolved');
    },
    // In the popup layout, the line saying it was resolved — the newest one
    // — is where the `closed` slot goes.
    closedEventId() {
      if (!this.popup || !this.resolved) return null;
      const line = [...this.messages].reverse().find((message) => message.event === 'resolved');
      return line ? line.id : null;
    },
    myReadAt() {
      if (!this.conversation) return null;
      return this.side === 'staff' ? this.conversation.staffLastReadAt : this.conversation.organizationLastReadAt;
    },
    theirReadAt() {
      if (!this.conversation) return null;
      return this.side === 'staff' ? this.conversation.organizationLastReadAt : this.conversation.staffLastReadAt;
    },
    messagesById() {
      const byId = {};
      this.messages.forEach((message) => {
        byId[message.id] = message;
      });
      return byId;
    },
    // What the composer shows above the box while replying.
    replyBar() {
      if (!this.replyingTo) return null;
      const quote = chatQuoteOf(this.messagesById[this.replyingTo.id] || this.replyingTo);
      return { quote, label: this.quoteAuthorLabel(quote) };
    },
    // Per person as well as per conversation: someone else signing in on the
    // same page never finds what the last person was typing.
    draftKey() {
      return `${this.myId || 'anyone'}:${this.side}:${this.threadId || `new:${this.organizationId || 'own'}`}`;
    },
    // The apps' small panel already says who is on the other end.
    placeholder() {
      if (this.popup) return this.resolved ? 'Write to start a new chat' : 'Write a message';
      return this.peerName && !this.compact ? `Message ${this.peerName}…` : 'Write a message…';
    },
    // The messages with a separator at each new day, one before the first
    // message that was new when the conversation came into view, and each
    // message told whether it starts or ends a run from the same person. The
    // lines the conversation keeps about itself stand on their own.
    entries() {
      const entries = [];
      const theirRead = chatDate(this.theirReadAt);
      const since = chatDate(this.unreadSince);
      let previous = null;
      let previousDay = null;
      let unreadPlaced = false;
      // Messages already listed: over the very first one, "New messages" only
      // marks where reading starts, unseen.
      let listed = 0;

      [...this.messages, ...this.pending].forEach((message) => {
        const at = chatDate(message.createdAt) || new Date();
        if (!previousDay || !isSameDay(previousDay, at)) {
          entries.push({ kind: 'day', key: `day-${at.toDateString()}`, label: chatDayLabel(at), today: isSameDay(at, new Date()) });
          previousDay = at;
          previous = null;
        }

        if (message.event && message.id === this.closedEventId) {
          entries.push({ kind: 'closed', key: `closed-${message.id}`, label: this.eventLabel(message), time: chatClock(message.createdAt) });
          previous = null;
          return;
        }
        if (message.event) {
          entries.push({
            kind: 'event',
            key: message.id,
            event: message.event,
            icon: message.event === 'resolved' ? 'pi-check' : 'pi-replay',
            label: this.eventLabel(message),
            time: chatClock(message.createdAt),
          });
          previous = null;
          return;
        }

        const mine = message.authorSide === this.side;
        if (this.showUnread && !unreadPlaced && !mine && !message.local && (!since || at > since)) {
          entries.push({ kind: 'unread', key: 'unread', quiet: listed === 0 && !this.hasMore });
          unreadPlaced = true;
          previous = null;
        }

        const author = `${message.authorSide}:${message.authorId || ''}`;
        const first = !previous || previous.author !== author || at - previous.at > GROUP_MS;
        if (!first) entries[entries.length - 1].last = false;

        // Written by the viewer, on this side. A Screenkode staff account can
        // also have written on its organization's side from the app: there it
        // is the organization's message, not theirs to change from here.
        const isMe = Boolean(message.local) || Boolean(message.authorId && message.authorId === this.myId && mine);
        const quote = this.quoteFor(message);
        entries.push({
          kind: 'message',
          key: message.clientId || message.id,
          message,
          mine,
          isMe,
          first,
          last: true,
          authorLabel: isMe ? '' : this.authorLabelOf(message),
          avatar: mine ? null : this.avatarOf(message),
          seen: mine && !message.local && Boolean(theirRead && theirRead >= at),
          quote,
          quoteLabel: quote ? this.quoteAuthorLabel(quote) : '',
          highlighted: Boolean(this.highlightId && this.highlightId === message.id),
        });
        previous = { author, at };
        listed += 1;
      });

      // Resolved without its line loaded (an older page): the slot still ends it.
      if (this.popup && this.resolved && !this.closedEventId && !this.loading) {
        entries.push({ kind: 'closed', key: 'closed', label: 'This conversation has been resolved', time: '' });
      }

      // A conversation that is all from today needs no "Today" over it: each
      // run says its time.
      const days = entries.filter((entry) => entry.kind === 'day');
      if (days.length === 1 && days[0].today && !this.hasMore) entries.splice(entries.indexOf(days[0]), 1);

      return entries;
    },
  },
  watch: {
    active(isActive) {
      if (isActive) this.activate();
      else this.deactivate();
    },
    live() {
      this.schedule();
    },
    // Another conversation asked for in the same place.
    conversationId(id) {
      if ((id || null) !== this.threadId) this.switchTo(id || null);
    },
    // Resolved while it was open: what was under way in the composer stops.
    resolved(isResolved) {
      if (!isResolved) return;
      this.editing = null;
      this.replyingTo = null;
      this.reactionPicker = null;
      this.typing = null;
    },
  },
  methods: {
    /* ------------------------------ people ------------------------------- */
    authorLabelOf(message) {
      const person = message.author;
      if (message.authorSide === 'staff') {
        return this.side === 'staff' ? chatPersonName(person, 'Screenkode') : chatFirstName(person, this.peerName || 'Support');
      }
      return chatPersonName(person, this.side === 'staff' ? this.peerName : 'Your team');
    },
    avatarOf(message) {
      if (this.side === 'organization' && this.peerAvatar) return this.peerAvatar;
      return { name: chatPersonName(message.author, this.peerName) };
    },
    // "Abel marked this conversation as resolved", "You reopened this conversation".
    eventLabel(message) {
      const isMe = this.side === 'staff' && message.authorId && message.authorId === this.myId;
      let who = 'You';
      if (!isMe) who = this.side === 'staff' ? chatPersonName(message.author, 'Screenkode') : chatFirstName(message.author, 'Support');
      return message.event === 'resolved' ? `${who} marked this conversation as resolved` : `${who} reopened this conversation`;
    },

    /* ------------------------------ replies ------------------------------ */
    // The quote a reply shows: worked out from the original when it is loaded
    // (so an edit or a delete shows at once), else as the API sent it.
    quoteFor(message) {
      if (!message || message.isDeleted || !message.replyToId) return null;
      const original = this.messagesById[message.replyToId];
      if (original) return chatQuoteOf(original);
      return message.replyTo || null;
    },
    // Who wrote a quoted message, as this side knows them — "You" for the
    // viewer's own.
    quoteAuthorLabel(quote) {
      if (quote.authorId && quote.authorId === this.myId && quote.authorSide === this.side) return 'You';
      return this.authorLabelOf({ authorSide: quote.authorSide, author: quote.author });
    },
    startReply(message) {
      if (!message || message.local || message.isDeleted || message.event || this.resolved) return;
      this.editing = null;
      this.replyingTo = message;
      this.$nextTick(() => this.$refs.composer?.focus(true));
    },
    messageElement(id) {
      const scroller = this.$refs.scroller;
      if (!scroller || !id) return null;
      return Array.from(scroller.querySelectorAll('[data-message-id]')).find((element) => element.getAttribute('data-message-id') === id) || null;
    },
    // A quote leads to its message: older pages load until it is there, then
    // it is scrolled to and flashes.
    async jumpTo(id) {
      if (!id) return;
      let element = this.messageElement(id);
      for (let pages = 0; !element && this.hasMore && pages < 25 && !this.isGone; pages += 1) {
        await this.loadOlder();
        await this.$nextTick();
        element = this.messageElement(id);
      }
      if (this.isGone) return;
      if (!element) {
        this.$toast.info("That message isn't in the conversation any more.", TOAST_GLOBAL_CONFIG);
        return;
      }
      element.scrollIntoView({ block: 'center', behavior: 'smooth' });
      this.highlightId = null;
      this.$nextTick(() => {
        this.highlightId = id;
        window.clearTimeout(this.highlightTimer);
        this.highlightTimer = window.setTimeout(() => {
          this.highlightId = null;
        }, 1800);
      });
    },

    /* ----------------------------- reactions ----------------------------- */
    // Shows at once, then as the API has it — or as it was, when it is refused.
    async react(message, emoji) {
      if (!message || message.local || message.isDeleted || message.event || this.resolved) return;
      const before = Array.isArray(message.reactions) ? message.reactions : [];
      this.replaceMessage({ ...message, reactions: chatReactionsAfter(before, emoji) });
      const generation = this.generation;
      const result = await reactToChatMessage(message.id, emoji);
      if (this.isGone || generation !== this.generation) return;
      if (result.ok && result.data) {
        this.absorb({ messages: [result.data] });
        return;
      }
      const current = this.messagesById[message.id];
      if (current) this.replaceMessage({ ...current, reactions: before });
      // Resolved or deleted meanwhile: the conversation catches up and says so.
      this.syncSoon();
    },
    replaceMessage(message) {
      this.messages = this.messages.map((item) => (item.id === message.id ? message : item));
    },
    // The whole emoji picker, by the message — above it, or below it when
    // there is no room above — and always inside the conversation.
    openReactionPicker(message, rect) {
      const viewport = this.$refs.viewport;
      if (!viewport || !message || this.resolved) return;
      const screen = viewport.getBoundingClientRect();
      // On-screen boxes are scaled when the page is zoomed (the apps'
      // html.app-zoomed); the picker is placed in the viewport's own pixels.
      const zoom = viewport.offsetWidth ? screen.width / viewport.offsetWidth : 1;
      const box = { width: viewport.offsetWidth || screen.width, height: viewport.offsetHeight || screen.height };
      let size = this.compact ? COMPACT_PICKER : PICKER;
      if (this.popup) size = POPUP_PICKER;
      const width = Math.min(size.width, box.width - 16);
      const height = Math.min(size.height, box.height - 16);
      const anchor = rect
        ? {
            top: (rect.top - screen.top) / zoom,
            bottom: (rect.bottom - screen.top) / zoom,
            left: (rect.left - screen.left) / zoom,
            right: (rect.right - screen.left) / zoom,
          }
        : { top: box.height / 2, bottom: box.height / 2, left: 8, right: box.width - 8 };
      let top = anchor.top - height - 8;
      if (top < 8) top = anchor.bottom + 8;
      top = Math.max(8, Math.min(top, box.height - height - 8));
      let left = message.authorSide === this.side ? anchor.right - width : anchor.left;
      left = Math.max(8, Math.min(left, box.width - width - 8));
      this.reactionPicker = {
        messageId: message.id,
        // The compact grid is as tall as its rows.
        style: this.popup
          ? { top: `${top}px`, left: `${left}px`, width: `${width}px` }
          : { top: `${top}px`, left: `${left}px`, width: `${width}px`, height: `${height}px` },
      };
    },
    pickReaction(emoji) {
      const message = this.reactionPicker ? this.messagesById[this.reactionPicker.messageId] : null;
      this.reactionPicker = null;
      if (message) this.react(message, emoji);
    },

    /* ----------------------------- loading ------------------------------- */
    setConversation(conversation) {
      if (!conversation) return;
      this.conversation = { ...(this.conversation || {}), ...conversation };
      this.$emit('conversation', this.conversation, this.organization);
    },
    // Messages as the API sent them, merged over what is shown: new ones
    // added, changed ones replaced, oldest first.
    absorb(data) {
      if (data.organization) this.organization = data.organization;
      if (data.conversation) this.setConversation(data.conversation);
      const incoming = Array.isArray(data.messages) ? data.messages : [];
      if (!incoming.length) return [];

      const known = new Map(this.messages.map((message) => [message.id, message]));
      const added = incoming.filter((message) => !known.has(message.id));
      incoming.forEach((message) => known.set(message.id, message));
      this.messages = Array.from(known.values()).sort(byCreatedAt);

      // A message sent from here that arrived by a sync before its own answer.
      const arrived = new Set(incoming.map((message) => message.clientId).filter(Boolean));
      if (arrived.size) {
        this.pending = this.pending.filter((entry) => {
          if (!arrived.has(entry.clientId)) return true;
          this.releaseLater(entry);
          return false;
        });
      }
      return added;
    },
    async load() {
      const generation = this.generation;
      this.loadFailed = false;
      if (!this.threadId) {
        // A new conversation: nothing to load until its first message.
        this.loading = false;
        this.hasMore = false;
        this.syncedAt = null;
        return;
      }
      this.loading = true;
      const result = await fetchChatConversation({ conversationId: this.threadId, limit: CHAT_PAGE_SIZE });
      if (this.isGone || generation !== this.generation) return;
      this.loading = false;
      if (!result.ok || !result.data) {
        this.loadFailed = true;
        return;
      }
      this.messages = [];
      this.absorb(result.data);
      this.hasMore = Boolean(result.data.hasMore);
      this.syncedAt = result.data.syncedAt || null;
      if (this.active) this.noteUnread();
      await this.$nextTick();
      this.scrollToStart();
      this.maybeMarkRead();
    },
    /** What changed since the last sync: new messages, edits, deletes, reactions, read marks, the status. */
    async sync() {
      if (this.isGone || this.loading || this.syncing || !this.threadId) return;
      if (!this.syncedAt) {
        await this.load();
        return;
      }
      const generation = this.generation;
      this.syncing = true;
      try {
        const result = await fetchChatConversation({ conversationId: this.threadId, since: this.syncedAt });
        if (this.isGone || generation !== this.generation || !result.ok || !result.data) return;
        if (result.data.reset) {
          await this.load();
          return;
        }
        const added = this.absorb(result.data);
        this.syncedAt = result.data.syncedAt || this.syncedAt;
        const fromThem = added.filter((message) => message.authorSide === this.otherSide && !message.event);
        if (fromThem.length) this.typing = null;
        // Catching up on coming into view: activate() places the reader.
        if (this.catchingUp) return;
        await this.$nextTick();
        if (added.length) {
          if (this.stickToBottom) this.scrollToBottom(true);
          else if (fromThem.length) {
            this.jumpCount += fromThem.length;
            this.showJump = true;
          }
        }
        this.maybeMarkRead();
      } finally {
        this.syncing = false;
      }
    },
    syncSoon() {
      window.clearTimeout(this.syncSoonTimer);
      this.syncSoonTimer = window.setTimeout(() => this.sync(), 120);
    },
    async loadOlder() {
      const oldest = this.messages[0];
      if (this.loadingOlder || !this.hasMore || !oldest || !this.threadId) return;
      const generation = this.generation;
      this.loadingOlder = true;
      const scroller = this.$refs.scroller;
      const fromBottom = scroller ? scroller.scrollHeight - scroller.scrollTop : 0;
      const result = await fetchChatConversation({ conversationId: this.threadId, before: oldest.createdAt, limit: CHAT_PAGE_SIZE });
      if (this.isGone || generation !== this.generation) return;
      this.loadingOlder = false;
      if (!result.ok || !result.data) return;
      this.absorb({ messages: result.data.messages });
      this.hasMore = Boolean(result.data.hasMore);
      await this.$nextTick();
      if (scroller) scroller.scrollTop = scroller.scrollHeight - fromBottom;
    },
    // Another conversation in the same place: everything of the last one goes.
    switchTo(id) {
      this.generation += 1;
      this.pending.forEach((entry) => this.release(entry));
      this.threadId = id;
      this.messages = [];
      this.pending = [];
      this.conversation = null;
      this.organization = null;
      this.hasMore = false;
      this.loadingOlder = false;
      this.syncedAt = null;
      this.unreadSince = null;
      this.showUnread = false;
      this.stickToBottom = true;
      this.showJump = false;
      this.jumpCount = 0;
      this.typing = null;
      this.editing = null;
      this.replyingTo = null;
      this.highlightId = null;
      this.lightbox = null;
      this.reactionPicker = null;
      this.markingFor = null;
      this.starting = null;
      this.load().then(() => this.schedule());
    },
    // Polling: often without a live connection, rarely with one, never out of view.
    schedule() {
      window.clearTimeout(this.pollTimer);
      if (this.isGone || !this.active) return;
      this.pollTimer = window.setTimeout(async () => {
        if (visible()) await this.sync();
        this.schedule();
      }, this.live ? CHAT_THREAD_LIVE_POLL_MS : CHAT_THREAD_POLL_MS);
    },
    // Came into view (the app's panel opened): catch up, mark where the new
    // messages start, scroll there, and read them — in that order, so the
    // line is drawn before reading moves the mark past it.
    async activate() {
      this.schedule();
      if (this.loading) return;
      this.catchingUp = true;
      try {
        await this.sync();
      } finally {
        this.catchingUp = false;
      }
      if (this.isGone || !this.active) return;
      this.noteUnread();
      await this.$nextTick();
      this.scrollToStart();
      this.maybeMarkRead();
    },
    deactivate() {
      window.clearTimeout(this.pollTimer);
      this.showUnread = false;
      this.typing = null;
      this.lightbox = null;
      this.reactionPicker = null;
    },
    noteUnread() {
      this.showUnread = Number(this.conversation?.unreadCount) > 0;
      this.unreadSince = this.myReadAt;
    },

    /* ------------------------------- reads ------------------------------- */
    // Tells the API this side has read up to the newest message from the
    // other side — when that is newer than its read mark, and only while the
    // conversation is in view.
    async maybeMarkRead() {
      if (!this.active || !visible() || this.loading || this.isGone || !this.threadId) return;
      const newest = [...this.messages].reverse().find((message) => message.authorSide === this.otherSide && !message.isDeleted && !message.event);
      if (!newest) return;
      const readAt = chatDate(this.myReadAt);
      const at = chatDate(newest.createdAt);
      if (!at || (readAt && readAt >= at) || this.markingFor === newest.id) return;

      const generation = this.generation;
      this.markingFor = newest.id;
      const result = await markChatRead({ conversationId: this.threadId, upTo: newest.createdAt });
      if (this.isGone || generation !== this.generation) return;
      if (result.ok && result.data && result.data.readAt) {
        const column = this.side === 'staff' ? 'staffLastReadAt' : 'organizationLastReadAt';
        this.setConversation({ [column]: result.data.readAt, unreadCount: 0 });
        noteChatRead(this.threadId);
        this.$emit('read', result.data);
      } else {
        this.markingFor = null;
      }
    },

    /* ---------------------------- live notices --------------------------- */
    onLiveEvent(event) {
      if (!this.threadId || event.conversationId !== this.threadId) return;

      if (event.type === 'typing') {
        if (event.side === this.otherSide && this.active && !this.resolved) this.showTyping(event.name);
        return;
      }
      if (event.type === 'message' && event.side === this.otherSide) this.typing = null;
      if (this.active) this.syncSoon();
    },
    showTyping(name) {
      this.typing = { name: name || this.peerName || 'Someone' };
      window.clearTimeout(this.typingTimer);
      this.typingTimer = window.setTimeout(() => {
        this.typing = null;
      }, TYPING_SHOWS_MS);
    },
    announceTyping() {
      if (!this.threadId) return;
      sendChatTyping({
        conversationId: this.threadId,
        organizationId: this.side === 'staff' ? this.organizationId || this.conversation?.organizationId || null : null,
      });
    },
    onVisibilityChange() {
      if (visible() && this.active) {
        this.sync();
        this.maybeMarkRead();
      }
    },

    /* ------------------------------ sending ------------------------------ */
    send({ body, files, voice }) {
      // Written in a resolved conversation (the popup layout keeps its
      // composer): a new conversation, started by this message, takes its place.
      if (this.resolved && this.popup) {
        this.switchTo(null);
        this.$emit('restarted');
      }
      const clientId = chatClientId();
      const replyToId = this.replyingTo && this.threadId ? this.replyingTo.id : null;
      const objectUrls = [];
      const attachments = (files || []).map((file) => {
        const url = URL.createObjectURL(file);
        objectUrls.push(url);
        return { kind: isChatImage(file.name, file.type) ? 'image' : 'file', name: file.name, url, size: file.size, mimetype: file.type };
      });
      if (voice) {
        const url = URL.createObjectURL(voice.blob);
        objectUrls.push(url);
        attachments.push({ kind: 'voice', name: voice.name, url, size: voice.blob.size, mimetype: voice.blob.type, duration: voice.duration, waveform: voice.waveform });
      }

      const entry = reactive({
        id: `local-${clientId}`,
        clientId,
        local: true,
        status: 'sending',
        progress: 0,
        authorSide: this.side,
        authorId: this.myId,
        author: this.account ? { id: this.account.id, fname: this.account.fname, lname: this.account.lname } : null,
        body: body || '',
        attachments,
        replyToId,
        reactions: [],
        isDeleted: false,
        editedAt: null,
        createdAt: new Date().toISOString(),
        // What goes to the API, kept for a retry. Files are never made reactive.
        outgoing: markRaw({ body: body || '', files: files || [], voice: voice || null, objectUrls, replyToId }),
      });
      this.replyingTo = null;
      this.pending.push(entry);
      this.stickToBottom = true;
      this.$nextTick(() => this.scrollToBottom(true));
      this.deliver(entry);
    },
    // Sends a message. The first one of a new conversation starts it; any
    // sent while that is on its way wait for it, so they land in the same
    // conversation rather than start more.
    async deliver(entry) {
      if (!entry || !entry.outgoing) return;
      const generation = this.generation;
      entry.status = 'sending';
      entry.progress = 0;

      if (!this.threadId && this.starting && this.starting.entry !== entry) {
        await this.starting.done;
        if (this.isGone || generation !== this.generation) return;
      }

      const starts = !this.threadId;
      let finished = null;
      if (starts) {
        this.starting = { entry, done: new Promise((resolve) => { finished = resolve; }) };
      }

      const { body, files, voice, replyToId } = entry.outgoing;
      let result = { ok: false, data: null };
      try {
        result = await sendChatMessage(
          {
            conversationId: this.threadId,
            newConversation: starts,
            organizationId: this.organizationId,
            body,
            files,
            voice,
            clientId: entry.clientId,
            replyToId: starts ? null : replyToId,
          },
          (fraction) => {
            entry.progress = fraction;
          }
        );
      } finally {
        if (starts) {
          if (this.starting && this.starting.entry === entry) this.starting = null;
          finished();
        }
      }
      if (this.isGone || generation !== this.generation) return;

      if (!result.ok || !result.data || !result.data.message) {
        entry.status = 'failed';
        // Refused because it was resolved meanwhile: the conversation says so.
        if (this.threadId) this.syncSoon();
        return;
      }
      if (starts && result.data.conversation && !this.threadId) {
        this.threadId = result.data.conversation.id;
        this.syncedAt = result.data.syncedAt || null;
        this.$emit('started', result.data.conversation);
      }
      this.pending = this.pending.filter((item) => item.clientId !== entry.clientId);
      this.releaseLater(entry);
      this.absorb({ conversation: result.data.conversation, messages: [result.data.message] });
      this.$emit('sent', result.data.message);
      refreshChatUnread();
    },
    discard(entry) {
      this.pending = this.pending.filter((item) => item.clientId !== entry.clientId);
      this.release(entry);
    },
    release(entry) {
      (entry?.outgoing?.objectUrls || []).forEach((url) => URL.revokeObjectURL(url));
    },
    releaseLater(entry) {
      window.setTimeout(() => this.release(entry), RELEASE_LOCAL_FILES_MS);
    },

    /* --------------------------- edit and delete -------------------------- */
    startEdit(message) {
      if (this.resolved) return;
      this.replyingTo = null;
      this.editing = message;
    },
    async saveEdit(body) {
      const target = this.editing;
      if (!target) return;
      if (body === String(target.body || '').trim()) {
        this.editing = null;
        return;
      }
      const result = await editChatMessage(target.id, body);
      if (this.isGone) return;
      if (result.ok && result.data) {
        this.absorb({ messages: [result.data] });
        this.editing = null;
      } else {
        this.syncSoon();
      }
    },
    async remove(message) {
      if (this.editing && this.editing.id === message.id) this.editing = null;
      if (this.replyingTo && this.replyingTo.id === message.id) this.replyingTo = null;
      if (this.reactionPicker && this.reactionPicker.messageId === message.id) this.reactionPicker = null;
      const result = await deleteChatMessage(message.id);
      if (this.isGone) return;
      if (result.ok && result.data) this.absorb({ messages: [result.data] });
    },
    async copy(message) {
      const text = String(message.body || '');
      try {
        await navigator.clipboard.writeText(text);
      } catch (error) {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        document.execCommand('copy');
        document.body.removeChild(area);
      }
      this.$toast.success('Copied.', { ...TOAST_GLOBAL_CONFIG, duration: 1500 });
    },
    /** For the parent: text into the composer (suggested questions and greetings). */
    insertText(text) {
      this.$refs.composer?.insertText(text);
    },
    /** For the parent: the cursor into the composer. */
    focus(evenOnTouch = false) {
      this.$refs.composer?.focus(evenOnTouch);
    },

    /* ----------------------------- scrolling ----------------------------- */
    scrollToBottom(smooth = false) {
      const scroller = this.$refs.scroller;
      if (!scroller) return;
      if (smooth && typeof scroller.scrollTo === 'function') scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
      else scroller.scrollTop = scroller.scrollHeight;
      this.stickToBottom = true;
      this.showJump = false;
      this.jumpCount = 0;
    },
    // Where reading starts: the "New messages" line when it is above the
    // fold, else the newest message.
    scrollToStart() {
      const scroller = this.$refs.scroller;
      const unread = Array.isArray(this.$refs.unread) ? this.$refs.unread[0] : this.$refs.unread;
      if (scroller && unread && scroller.scrollHeight - unread.offsetTop > scroller.clientHeight) {
        scroller.scrollTop = Math.max(unread.offsetTop - 12, 0);
        this.stickToBottom = false;
        return;
      }
      this.scrollToBottom(false);
    },
    onScroll() {
      const scroller = this.$refs.scroller;
      if (!scroller) return;
      const gap = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      this.stickToBottom = gap < STICK_PX;
      if (this.stickToBottom) {
        this.showJump = false;
        this.jumpCount = 0;
      }
      if (scroller.scrollTop < OLDER_PX && this.hasMore && !this.loadingOlder && !this.loading) this.loadOlder();
    },
    // A picture loaded after the conversation was scrolled: stay at the end.
    onMediaLoad() {
      if (this.stickToBottom) this.scrollToBottom(false);
    },

    /* ------------------------------- drops ------------------------------- */
    onDragEnter(event) {
      if (hasFiles(event) && !this.resolved) this.dragDepth += 1;
    },
    onDragOver(event) {
      if (event.dataTransfer) event.dataTransfer.dropEffect = hasFiles(event) && !this.resolved ? 'copy' : 'none';
    },
    onDragLeave() {
      if (this.dragDepth > 0) this.dragDepth -= 1;
    },
    onDrop(event) {
      this.dragDepth = 0;
      if (this.resolved) return;
      const files = event.dataTransfer?.files;
      if (files && files.length) this.$refs.composer?.addFiles(files);
    },
  },
  created() {
    // Plain fields: nothing in the template reads them.
    this.isGone = false;
    this.syncing = false;
    this.catchingUp = false;
    this.markingFor = null;
    this.pollTimer = null;
    this.syncSoonTimer = null;
    this.typingTimer = null;
    this.highlightTimer = null;
    // Bumped when another conversation is shown here: answers about the last one are dropped.
    this.generation = 0;
    // The first message of a new conversation while it is on its way: { entry, done }.
    this.starting = null;
  },
  async mounted() {
    this.stopListening = onChatEvent(this.onLiveEvent);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    if (typeof ResizeObserver !== 'undefined') {
      // Whatever changes size — a picture, the typing line, a taller composer
      // — the end of the conversation stays in view while it was.
      this.resizeObserver = new ResizeObserver(() => {
        if (this.stickToBottom) this.scrollToBottom(false);
      });
      if (this.$refs.scroller) this.resizeObserver.observe(this.$refs.scroller);
      if (this.$refs.content) this.resizeObserver.observe(this.$refs.content);
    }
    await this.load();
    this.schedule();
  },
  beforeUnmount() {
    this.isGone = true;
    window.clearTimeout(this.pollTimer);
    window.clearTimeout(this.syncSoonTimer);
    window.clearTimeout(this.typingTimer);
    window.clearTimeout(this.highlightTimer);
    if (this.stopListening) this.stopListening();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.pending.forEach((entry) => this.release(entry));
  },
};
</script>
